import { useId, useLayoutEffect, useRef, useState } from "react";
import { TbArrowUpRight } from "react-icons/tb";
import { Link } from "react-router";

/**
 * Gap (px) between the label's edges and the banner's cutout edges.
 * Responsive: tighter on small screens, roomier once the viewport widens.
 */
const CUTOUT_GAP_NARROW = 6;
const CUTOUT_GAP_WIDE = 9;
/** Viewport width past which the wider gap applies (Tailwind's `sm`, 640px). */
const WIDE_VIEWPORT_QUERY = "(min-width: 640px)";

// Hover/press shadows as drop-shadow filters so they trace the clipped shape
// (including the notch) rather than the full rectangle a box-shadow would draw.
const REST_SHADOW = "drop-shadow(0 0 0 rgba(23,37,84,0))";
const HOVER_SHADOW = "drop-shadow(0 10px 16px rgba(23,37,84,0.12))";
const PRESS_SHADOW = "drop-shadow(0 16px 26px rgba(23,37,84,0.18))";

export function LatestUpdateBanner() {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const bannerRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const clipId = `banner-clip-${useId().replace(/:/g, "")}`;
  const [clipPath, setClipPath] = useState<string>();

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    const label = labelRef.current;
    if (!banner || !label) return;

    const mql = window.matchMedia(WIDE_VIEWPORT_QUERY);

    const update = () => {
      const w = banner.offsetWidth;
      const h = banner.offsetHeight;
      const gap = mql.matches ? CUTOUT_GAP_WIDE : CUTOUT_GAP_NARROW;
      // Notch dimensions = label size + gap so the gradient frames the label.
      const cw = label.offsetWidth + gap;
      const ch = label.offsetHeight + gap;
      // Convex radius tracks the banner's live corner radius (6 → 8 across
      // breakpoints). The notch's inner concave corner is kept at the original
      // radius (12 → 16) — i.e. twice the convex value.
      const r = parseFloat(getComputedStyle(banner).borderTopRightRadius) || 8;
      const ri = r * 2;

      // Trace the visible banner region (full rect minus the top-left notch),
      // clockwise. Outer corners stay square — CSS border-radius rounds those.
      // Notch: convex corners at (cw,0) and (0,ch), concave corner at (cw,ch).
      const d = [
        `M ${cw + r} 0`, // top edge, right of the notch's top corner
        `L ${w} 0`,
        `L ${w} ${h}`,
        `L 0 ${h}`,
        `L 0 ${ch + r}`, // up the left edge to the notch's bottom corner
        `Q 0 ${ch} ${r} ${ch}`, // convex round at (0, ch)
        `L ${cw - ri} ${ch}`, // along the notch's bottom edge
        `Q ${cw} ${ch} ${cw} ${ch - ri}`, // concave round at (cw, ch)
        `L ${cw} ${r}`, // up the notch's right edge
        `Q ${cw} 0 ${cw + r} 0`, // convex round at (cw, 0)
        "Z",
      ].join(" ");

      // Identical `d` → React bails out of the re-render, so no visual churn.
      setClipPath(d);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    observer.observe(label);
    // Recompute when the viewport crosses the breakpoint (narrow ⇄ wide gap).
    mql.addEventListener("change", update);
    return () => {
      observer.disconnect();
      mql.removeEventListener("change", update);
    };
  }, []);

  const clipStyle = clipPath
    ? { clipPath: `url(#${clipId})`, WebkitClipPath: `url(#${clipId})` }
    : undefined;

  return (
    <Link
      to="/updates/37ad6705ffba8000bccde76aba415a69"
      className="relative mb-16 block md:mb-20"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      {/* Cutout clip path — lives in the DOM so updates mutate an attribute
          (no image decode) instead of swapping a data-URI mask. */}
      <svg aria-hidden className="pointer-events-none absolute h-0 w-0">
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path d={clipPath} />
          </clipPath>
        </defs>
      </svg>

      {/* "Latest Updates" label */}
      <div
        ref={labelRef}
        className="bg-light absolute top-0 left-0 z-20 inline-flex items-center rounded-md px-5 py-2 sm:px-8 md:rounded-lg md:px-10 md:py-2.5"
      >
        <span className="font-impact text-nav-dark sm:text-medium text-sm font-medium md:text-lg">
          LATEST UPDATES:
        </span>
      </div>

      {/* Shadow wrapper — drop-shadow reads the clipped banner's alpha so the
          shadow follows the cutout instead of the full rectangle. */}
      <div
        className="transition-[filter] duration-200 ease-out"
        style={{
          filter: isPressed
            ? PRESS_SHADOW
            : isHovered
              ? HOVER_SHADOW
              : REST_SHADOW,
        }}
      >
        {/* Banner image */}
        <div
          ref={bannerRef}
          className="relative flex h-37 w-full items-end overflow-hidden rounded-md sm:h-40 md:h-45 md:rounded-lg xl:h-50"
          style={clipStyle}
        >
          <img
            src="https://storage.googleapis.com/turtlewave/public/art/compositions/highlights/37ad6705ffba8000bccde76aba415a69/preview.png"
            alt="Hello World: TurtleWave is Now Online"
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Update title */}
          <h2 className="relative z-1 p-4 text-xl font-bold text-[#f3f1f2] sm:p-5 sm:text-3xl md:p-6 md:text-4xl lg:font-semibold">
            <span className="text-medium font-semibold sm:text-xl md:text-2xl lg:font-medium">
              HELLO WORLD:
            </span>
            <br />
            TurtleWave is Now Online
            <TbArrowUpRight className="inline size-6 translate-y-[-0.07em] sm:size-9 md:size-12 md:translate-y-[-0.075em]" />
          </h2>

          {/* Hover overlay — clipped directly; its backdrop-filter otherwise
              escapes the parent's clip-path in Chrome. */}
          <div
            className={`absolute inset-0 bg-black/20 backdrop-blur-[0.5px] transition-opacity duration-200 ease-out ${isHovered ? "opacity-100" : "pointer-events-none opacity-0"}`}
            style={clipStyle}
          />
        </div>
      </div>
    </Link>
  );
}
